import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import apiService from "../../../Services/authenticationServices";
import { useCheOrders } from "./CheOrders";
import { toast } from "react-toastify";
import { convertUTCDate, getPrescriptionSrcType } from "../../../Utils/utilServices";
import { HiDownload, HiX } from "react-icons/hi";
import { convertAmountToWords } from "../../../Utils/utilServices";
import CureBaylogo from "../../../Assets/CurebayLogo.svg";
import bulkOrderIcon from '../../../Assets/BulkOrder.png'
import ShipmentBarcode from "../../ShipmentBarcode";

const PrescriptionItem = ({ item, index }) => {
  const [currentUrl, setCurrentUrl] = useState(item.fallbackUrl ? null : item.url);
  const [isLoading, setIsLoading] = useState(!!item.fallbackUrl);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!item.fallbackUrl) {
      setCurrentUrl(item.url);
      setIsLoading(false);
      return;
    }

    const checkUrl = async (url) => {
      try {
        const response = await fetch(url, { method: 'GET' });
        if (response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType && !contentType.includes('xml')) {
            return true;
          }
        }
        return false;
      } catch {
        return false;
      }
    };

    const determineUrl = async () => {
      const primaryWorks = await checkUrl(item.url);
      if (primaryWorks) {
        setCurrentUrl(item.url);
      } else {
        const fallbackWorks = await checkUrl(item.fallbackUrl);
        if (fallbackWorks) {
          setCurrentUrl(item.fallbackUrl);
        } else {
          setHasError(true);
        }
      }
      setIsLoading(false);
    };

    determineUrl();
  }, [item.url, item.fallbackUrl]);

  const handleImageError = () => {
    if (item.fallbackUrl && currentUrl !== item.fallbackUrl) {
      setCurrentUrl(item.fallbackUrl);
    } else {
      setHasError(true);
    }
  };

  if (isLoading) {
    return (
      <div className="border rounded p-2 flex items-center justify-center h-[200px]">
        <p>Loading prescription...</p>
      </div>
    );
  }

  if (hasError || !currentUrl) {
    return (
      <div className="border rounded p-2 flex items-center justify-center h-[200px]">
        <p>Failed to load prescription</p>
      </div>
    );
  }
  return (
    <div className="border rounded p-2">
      {item.srcType.includes("image/") ? (
        <img
          src={item.url}
          alt={`Prescription ${index + 1}`}
          className="w-full h-auto mx-auto"
        />
      ) : (
        <iframe
          src={item.url}
          className="w-full h-[500px]"
          title={`Prescription ${index + 1}`}
        ></iframe>
      )}
    </div>
  );
};


const InvoiceModal = ({ isOpen, onClose, orderData, orderDetails }) => {
  if (!isOpen || !orderData) return null;

  const totalAmount = orderDetails?.reduce(
    (sum, item) => sum + item.unitPrice * item.quantity,
    0
  ) || 0;

  const discount = totalAmount - orderData.paidAmount;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${date.toLocaleDateString("en-GB")} ${date.toLocaleTimeString()}`;
  };

  const formatAddress = (address1, address2, city, state, pinCode) => {
    return [address1, address2, city, state, pinCode].filter(Boolean).join(", ");
  };

  const amountInWords = orderData.paidAmount > 0
    ? `(${convertAmountToWords(orderData.paidAmount)})`
    : "";

  const downloadInvoice = () => {
    const printContents = document.getElementById('invoice-content').innerHTML;
    printDiv(printContents);
  };

  const printDiv = (div) => {
    let popupWin = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    popupWin.document.write(`
      <html>
        <head>
          <title>Invoice #${orderData.orderId}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              color: #333;
              line-height: 1.5;
            }
            .invoice-container {
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .header h1 {
              font-size: 24px;
              margin-bottom: 10px;
            }
            .patient-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
            }
            .patient-info-col {
              width: 48%;
            }
            .info-label {
              font-weight: bold;
              margin-bottom: 5px;
            }
            .info-value {
              margin-bottom: 10px;
            }
            .address {
              margin-bottom: 20px;
            }
            hr {
              border: 0;
              border-top: 1px solid #ddd;
              margin: 20px 0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f6f5f1;
              font-weight: bold;
            }
            .total-row {
              font-weight: bold;
            }
            .amount-in-words {
              font-style: italic;
              text-align: right;
              margin-top: 5px;
            }
            .d-print-none {
              display: none;
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            ${div}
          </div>
        </body>
      </html>
    `);
    popupWin.document.close();
    popupWin.focus();
    setTimeout(function () {
      popupWin.print();
      popupWin.close();
    }, 500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4">
      <div id="invoice-content" className="bg-white rounded-lg p-4 sm:p-8 w-full max-w-7xl relative overflow-scroll max-h-[90vh] print-content">

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl d-print-none"
        >
          <HiX />
        </button>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Order Invoice</h2>
          </div>
          <img
            src={CureBaylogo}
            alt="CureBay Logo"
            className="h-8"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
          <div>
            <p className="text-gray-600">Patient</p>
            <p className="font-semibold">
              {orderData.patientName}/{orderData.patientGender}/
              {orderData.patientAge} Years ({orderData.patientId})
            </p>
          </div>
          <div>
            <p className="text-gray-600">Mobile</p>
            <p className="font-semibold">{orderData.patientMobile}</p>
          </div>
          <div>
            <p className="text-gray-600">Order ID</p>
            <p className="font-semibold">{orderData.orderId}</p>
          </div>
          <div>
            <p className="text-gray-600">Ordered Date</p>
            <p className="font-semibold">{convertUTCDate(orderData.modifiedDate, 'MM/DD/YYYY hh:mm:ss')}</p>
          </div>
          <div>
            <p className="text-gray-600">Transaction ID</p>
            <p className="font-semibold">{orderData.refTxnId}</p>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-gray-600 text-sm">Address</p>
          <p className="font-semibold text-sm">
            {formatAddress(
              orderData.address1,
              orderData.address2,
              orderData.patientCityBook,
              orderData.patientStateBook,
              orderData.patientPinCodeBook
            )}
          </p>
        </div>

        <hr className="my-4" />

        <div className="overflow-x-auto text-sm mb-6">
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                {[
                  "Transaction Type",
                  "Transaction ID",
                  "Payable Amount",
                  "Date Of Payment",
                  "Payment Mode",
                  "Service Provider/Doctor",
                  "Discount Amount",
                ].map((header, idx) => (
                  <th key={idx} className="p-2 text-left border">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border">{orderData?.serviceName}</td>
                <td className="p-2 border">{orderData.refTxnId}</td>
                <td className="p-2 border">₹{orderData.paidAmount?.toFixed(2)}</td>
                <td className="p-2 border">{convertUTCDate(orderData.paymentDateTime, 'MM/DD/YYYY hh:mm:ss')}</td>
                <td className="p-2 border">{orderData.payMode || "Cash"}</td>
                <td className="p-2 border">{orderData.hospitalName}</td>
                <td className="p-2 border">₹{discount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="font-bold text-lg mb-2">Pharmacy Order Breakup</h3>
        <div className="overflow-x-auto text-sm mb-6">
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left border">Prescription Order</th>
                <th className="p-2 text-left border">Quantity</th>
                <th className="p-2 text-left border">Unit Price</th>
                <th className="p-2 text-left border">Total Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              {orderDetails?.map((item, index) => (
                <tr key={index}>
                  <td className="p-2 border">{item.drugName}</td>
                  <td className="p-2 border">{item.quantity}</td>
                  <td className="p-2 border">₹{item.unitPrice?.toFixed(2)}</td>
                  <td className="p-2 border">
                    ₹{(item.quantity * item.unitPrice)?.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end">
          <div className="w-full md:w-1/2 text-sm">
            <div className="flex justify-between mb-2 font-semibold">
              <span>Total (INR):</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between mb-2 font-semibold">
              <span>CureBay Discount (INR):</span>
              <span>₹{discount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t pt-2">
              <span>Payable Amount (INR):</span>
              <span>₹{orderData.paidAmount?.toFixed(2)}</span>
            </div>
            <p className="text-right italic text-xs text-gray-600 mt-1">
              {amountInWords}
            </p>
          </div>
        </div>

        <div className="flex justify-end mt-6 space-x-3 d-print-none">
          <button
            onClick={downloadInvoice}
            className="flex items-center gap-2 border border-blue-500 text-blue-600 px-4 py-2 rounded hover:bg-blue-100"
          >
            <HiDownload />
            Download
          </button>
          <button
            onClick={onClose}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderHeaderAccordion = ({ data, index, isOpen, onToggle, onClose }) => {
  const scrollRef = useRef(null);
  const bgColor = index % 2 === 0 ? "bg-[#f6f5f1]" : "bg-[#FFFFFF]";
  const [orderDetails, setorderDetails] = useState([]);
  const [enquiryId, setenquiryId] = useState(data.id);
  const [showPrescription, setShowPrescription] = useState(false);
  const [prescriptionUrls, setPrescriptionUrls] = useState([]);
  const [showInvalidConfirm, setShowInvalidConfirm] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedConsignment, setSelectedConsignment] = useState(null);

  const pdtStatuses = [
    { name: 'Yet to Packed', value: '' },
    { name: 'Packed', value: 1 },
    { name: 'Shipped', value: 2 },
    { name: 'Order on the Bus', value: 3 },
    { name: 'Out for Delivery', value: 4 },
    { name: 'Delivered', value: 5 }
  ];

  const slaList = [
    { name: '0 -12 Hours', value: 1 },
    { name: '12-24 Hours', value: 2 },
    { name: '24-36 Hours', value: 3 },
    { name: '36-48 Hours', value: 4 },
    { name: '48-60 Hours', value: 5 },
    { name: '60-72 Hours', value: 6 },
    { name: '72 Hrs-1 Week', value: 7 },
    { name: 'Cannot be supplied', value: 8 },
    { name: 'CureBay Preferred Medicine ', value: 9 },
  ];

  const columns = [
    { field: 'drugName', header: 'Drug Name' },
    { field: 'drugCode', header: 'Drug Code' },
    { field: 'drugsInfoComposition', header: 'Composition & Strength' },
    { field: 'drugsInfoTypeofsell', header: 'Package Size' },
    { field: 'quantity', header: 'Quantity' },
    { field: 'unitPrice', header: 'Unit Price' },
    { field: 'actualPrice', header: 'Payable Amount' },
    { field: 'consignmentStatus', header: 'Pdt.Status' },
    { field: 'deliverySlaStatus', header: 'SLA' },
    { field: 'deliverySlaTime', header: 'Committed ETA' },
    { field: 'deliverySlaETATime', header: 'ETA' },
    { field: 'consignmentId', header: 'Consignment ID' },
  ];

  const formatAddress = (...parts) => parts.filter(Boolean).join(", ");

  const fetchData = async () => {
    try {
      const payload = {
        patientDrugPrescriptionOrderId: data.orderId,
        status: 1,
      };

      const response = await apiService.getOrderDetails(payload);
      const enquiryList = response.data.successList;
      setorderDetails(enquiryList);
    } catch (err) {
      // toast.error("An error occurred while fetching order details.");
    } finally {
      // setLoading(false);
    }
  };

  const getSlaName = (value) => {
    const slaItem = slaList.find(item => item.value === value || '');
    return slaItem?.name ?? '';
  };

  const getStatusName = (value) => {
    const status = pdtStatuses.find(item => item.value == value);
    return status ? status.name : 'Yet to Packed';
  };

  const viewPrescription = async (orderData) => {
    const urls = [];

    // Pre-utility-service behaviour: try the "curebay-prescription-docs/"
    // prefixed URL first, with the bare bucket URL as fallbackUrl —
    // PrescriptionItem probes both and renders whichever actually loads.
    const buildLegacyEntry = (file) => {
      const extension = file.split(".").pop().toUpperCase();
      const primaryUrl = process.env.REACT_APP_API_GCP_URL + "curebay-prescription-docs/" + file;
      const fallbackUrl = process.env.REACT_APP_API_GCP_URL + file;

      switch (extension) {
        case "PDF":
          return { url: primaryUrl, fallbackUrl, srcType: 'application/pdf' };
        case "JPG":
          return { url: primaryUrl, fallbackUrl, srcType: 'image/jpg' };
        case "JPEG":
          return { url: primaryUrl, fallbackUrl, srcType: 'image/jpeg' };
        case "PNG":
          return { url: primaryUrl, fallbackUrl, srcType: 'image/png' };
        default:
          return { url: primaryUrl, fallbackUrl, srcType: 'application/octet-stream' };
      }
    };

    const processFiles = async (filesStr) => {
      if (!filesStr) return;
      const files = filesStr.split(",");

      const filePromises = files.map(async (file) => {
        if (!file) return;
        const response = await apiService.getDocumentUrl(file);
        if (response?.status === 200 && response?.data?.SIGNED_URL) {
          urls.push({ url: response.data.SIGNED_URL, srcType: getPrescriptionSrcType(file) });
        } else {
          console.warn("getDocumentUrl failed (500 or otherwise), falling back to the old REACT_APP_API_GCP_URL behavior for", file, response);
          urls.push(buildLegacyEntry(file));
        }
      });

      await Promise.all(filePromises);
    };

    await processFiles(orderData.prescriptionDoc);
    if (orderDetails.length > 0) {
      await processFiles(orderDetails[0].prescriptionUrl);
    }

    if (urls.length > 0) {
      setPrescriptionUrls(urls);
      setShowPrescription(true);
    } else {
      toast.error("No prescription available for this order");
    }
  };


  const isMedsNow = (data.shipments || []).some(
    (sh) => sh?.shipmentType === "EXPRESS_30"
  );

  const headerFields = [
    { label: "Order Date", value: data.modifiedDate, width: "sm:w-[12%]" },
    { label: "Order ID", value: data.orderId, width: "sm:w-[12%]" },
    { label: "Enquiry ID", value: data.enquiryId ?? "-", width: "sm:w-[10%]" },
    { label: "Patient ID", value: data.patientId, width: "sm:w-[12%]" },
    { label: "Patient Name", value: data.patientName, width: "sm:w-[12%]" },
    { label: "Mobile No.", value: data.patientMobile, width: "sm:w-[12%]" },
    {
      label: "Patient Address",
      value: formatAddress(data.address1, data.address2, data.address3),
      width: "sm:w-[20%]",
    },
    { label: "Eclinic", value: data.cheBranchName, width: "sm:w-[10%]" },
    {
      label: "Eclinic Address",
      value: formatAddress(
        data.deliveryAddress1,
        data.deliveryAddress2,
        data.deliveryAddress3
      ),
      width: "sm:w-[20%]",
    },
    { label: "Amount", value: data.paidAmount, width: "sm:w-[8%]" },
  ];

  const submitOrder = async (e) => {
    try {
      let payload = {}
      e == 'C' ? payload = {
        orderId: data.orderId,
        procedureStatus: 1,
        status: data.status
      } : payload = {
        orderId: data.orderId,
        procedureStatus: 0,
        status: 8
      }

      const res = await apiService.submitorderDetails(payload);
      if (res.data.responseCode === '200') {
        e == 'C' ? toast.success('Order Submitted Successfully') : toast.success('Order Updated Successfully')
        onClose();
        setShowInvalidConfirm(false)
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit order');
    }
  };

  useEffect(() => {
    if (isOpen) {
      scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      fetchData();
    }
  }, [isOpen, data.orderId]);

  return (
    <div ref={scrollRef} className={`relative border rounded ${bgColor} my-3`}>
      {isMedsNow && (
        <span className="absolute top-0 left-0 z-10 px-2 py-0.5 rounded-tl rounded-br bg-[#336b3b] text-white text-[10px] font-bold tracking-wide">
          MEDS NOW
        </span>
      )}
      <div
        onClick={onToggle}
        className="cursor-pointer px-2 sm:px-4 flex justify-between items-center"
      >
        <div className={`flex-1 sm:flex-initial min-w-0 flex flex-wrap sm:flex-nowrap overflow-auto text-xs p-2 sm:p-4 gap-x-3 gap-y-2 sm:gap-5 ${isMedsNow ? "pt-6 sm:pt-6" : ""}`}>
          {headerFields.map(({ label, value, width }, i) => (
            <span key={i} className={`w-[calc(50%-6px)] ${width} break-words`}>
              <span className="text-gray-500">{label}:</span> {value}
            </span>
          ))}
        </div>
        <span className="text-gray-600 text-xl">
          {isOpen ? <HiChevronUp /> : <HiChevronDown />}
        </span>
      </div>

      {isOpen && <div className="h-[1px] bg-gray-300 w-full" />}

      {isOpen && (
        <div className="p-4 pb-8 bg-white overflow-auto">
          <div className="flex flex-wrap sm:flex-nowrap overflow-auto gap-4 mb-4 text-sm">
            {data.defaultSla && (
              <div className="w-full sm:w-[15%] break-words">
                <p className="text-gray-500">
                  Delivery SLA: <span> {getSlaName(data.defaultSla)}
                  </span>                </p>
              </div>
            )}
            {data.shipTo && (
              <div className="w-full sm:w-[15%] break-words">
                <p className="text-gray-500">
                  Ship To: <span>{data.shipTo}</span>
                </p>
              </div>
            )}
            {data.shippingName && (
              <div className="w-full sm:w-[20%] break-words">
                <p className="text-gray-500">
                  Shipping Name: <span>{data.shippingName}</span>
                </p>
              </div>
            )}
            {data.shippingAddress && (
              <div className="w-full sm:w-[50%] break-words">
                <p className="text-gray-500">
                  Shipping Address: <span>{data.shippingAddress}</span>
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-between gap-2 mb-3">
            <div className="flex flex-wrap items-center gap-y-2">

              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md"
                onClick={() => setShowInvoiceModal(true)}
              >
                Open Invoice
              </button>
              {isMedsNow && (
                <span className="ml-3 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#336b3b] text-white text-[11px] font-bold tracking-wide">
                  <span className="h-1.5 w-1.5 rounded-full bg-white" />
                  MEDS NOW
                </span>
              )}
              {data.bulkOrderFlag === "Y" ? (
                <div className="flex items-center text-[#2e4373] font-medium ml-4">
                  <img
                    src={bulkOrderIcon}
                    alt="Bulk Order Icon"
                    className="h-6 w-6 mr-1"
                  />
                  Marked as Bulk Order
                </div>
              ) : (
                data.prescriptionDoc && (
                  <button
                    className="bg-blue-500 text-white px-4 py-2 rounded-md ml-4"
                    onClick={() => viewPrescription(data)}
                  >
                    View Prescription
                  </button>
                )
              )}
            </div>
          </div>

          {showPrescription && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-0">
              <div className="bg-white p-4 rounded-lg max-w-4xl w-full max-h-screen overflow-auto">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">Prescription</h2>
                  <button
                    onClick={() => setShowPrescription(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {prescriptionUrls.map((item, index) => (
                    <PrescriptionItem key={index} item={item} index={index} />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto border border-[#1d2f5b] rounded-md">
            <div className="min-w-full inline-block align-middle">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-[#1d2f5b] text-white">
                    <tr>
                      {columns.map((column, index) => (
                        <th key={index} className="p-2 font-semibold">
                          {column.header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orderDetails.map((item) => (
                      <tr key={item.id} className="border-t">
                        {columns.map((column) => {
                          if (column.field === 'deliverySlaStatus') {
                            return (
                              <td key={column.field} className="p-2 whitespace-nowrap">
                                {getSlaName(item.deliverySlaStatus ?? item.defaultSla)}
                              </td>
                            );
                          }
                          else if (column.field === 'deliverySlaETATime') {
                            const slaColor =
                              item.deliverySlaColourStatus === 2 ? 'green' :
                                item.deliverySlaColourStatus === 1 ? 'red' :
                                  '';

                            const style = {
                              color: slaColor,
                              fontWeight: 'bold'
                            }

                            return (
                              <td key={column.field} className="p-2 whitespace-nowrap" style={style}>
                                {item[column.field]}
                              </td>
                            );
                          }
                          else if (column.field === 'consignmentStatus') {
                            return (
                              <td key={column.field} className="p-2 whitespace-nowrap">
                                {getStatusName(item[column.field])}
                              </td>
                            );
                          }
                          else if (column.field === 'consignmentId') {
                            return (
                              <td key={column.field} className="p-2 relative">
                                {item[column.field] ? (
                                  <button
                                    className="text-blue-600 hover:underline"
                                    onClick={() => {
                                      console.log("Clicked consignment ID:", item[column.field]);
                                      console.log("Full item data:", item);
                                      setSelectedConsignment(item[column.field]);
                                    }}
                                  >
                                    {item[column.field]}
                                  </button>
                                ) : (
                                  "-"
                                )}
                              </td>
                            );
                          }
                          return (
                            <td key={column.field} className="p-2">
                              {item[column.field] ?? "-"}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        orderData={data}
        orderDetails={orderDetails}
      />
      <ShipmentBarcode
        isOpen={!!selectedConsignment}
        consignmentId={selectedConsignment}
        onClose={() => {
          console.log("Closing ShipmentBarcode modal");
          setSelectedConsignment(null);
        }}
      />
    </div>
  );
};

const ChePacked = forwardRef((props, ref) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [orders, setOrders] = useState([]);
  const [invalidList, setinvalidList] = useState([]);
  const [sortOrder, setSortOrder] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const { filter, setFilterField } = useCheOrders();

  const fetchData = async (pageNo = 1) => {
    try {
      let payload = {
        procedureStatus: -2,
        pageNumber: pageNo,
        orderBy: sortOrder === 0 ? 'ASC' : 'DESC',
        conStatus: 2,
        ...Object.fromEntries(
          Object.entries(filter).filter(
            ([_, val]) => val !== null && val !== undefined && val !== ""
          )
        ),
      };
      if (payload.fromDate && payload.toDate) {
        payload.fromDate = convertUTCDate(payload.fromDate);
        payload.toDate = convertUTCDate(payload.toDate);
      }
      const response = await apiService.getmedicineOrders(payload);
      let orderList = response.data.successList || [];

      setOrders(orderList);
      setCurrentPage(pageNo);
      setHasNextPage(orderList.length > 0);
    } catch (err) {
      console.error("Failed to fetch orders", err);
      setOrders([]);
      setHasNextPage(false);
    }
  };

  const handleNextPage = () => {
    fetchData(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      fetchData(currentPage - 1);
    }
  };

  useEffect(() => {
    fetchData(1);
  }, [filter, sortOrder]);

  useImperativeHandle(ref, () => ({
    onSearch: () => fetchData(1),
  }));

  const handleToggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const handleSortChange = (value) => {
    setSortOrder(value);
  };

  return (
    <div className="pl-1 pr-1 sm:pr-6 min-h-screen">
      <div className="flex flex-wrap items-center gap-4 ml-4 mb-4">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="sortOrder"
            value="newest"
            checked={sortOrder === 1}
            onChange={() => handleSortChange(1)}
            className="h-4 w-4 text-blue-600"
          />
          <span>Newest First</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="sortOrder"
            value="oldest"
            checked={sortOrder === 0}
            onChange={() => handleSortChange(0)}
            className="h-4 w-4 text-blue-600"
          />
          <span>Oldest First</span>
        </label>
      </div>

      {orders.map((order, index) => (
        <OrderHeaderAccordion
          key={index}
          data={order}
          index={index}
          isOpen={openIndex === index}
          onToggle={() => handleToggle(index)}
          onClose={fetchData}
        />
      ))}

      <div className="flex flex-wrap justify-center items-center mt-4 mb-8 gap-2">
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className="px-4 py-2 border rounded disabled:opacity-50"
        >
          Previous
        </button>

        <button
          onClick={() => fetchData(1)}
          className={`px-4 py-2 border rounded ${currentPage === 1 ? 'bg-blue-100' : ''}`}
        >
          1
        </button>

        {currentPage > 1 && (
          <button
            onClick={() => fetchData(currentPage)}
            className="px-4 py-2 border rounded bg-blue-100"
          >
            {currentPage}
          </button>
        )}

        <button
          onClick={handleNextPage}
          disabled={!hasNextPage}
          className="px-4 py-2 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
});

export default ChePacked;